// SPDX-License-Identifier: AGPL-3.0-or-later

import {handleChannelCreate} from '@app/features/channel/events/ChannelCreate';
import {handleChannelDelete} from '@app/features/channel/events/ChannelDelete';
import {handleChannelPinsAck} from '@app/features/channel/events/ChannelPinsAck';
import {handleChannelPinsUpdate} from '@app/features/channel/events/ChannelPinsUpdate';
import {handleChannelRecipientAdd} from '@app/features/channel/events/ChannelRecipientAdd';
import {handleChannelRecipientRemove} from '@app/features/channel/events/ChannelRecipientRemove';
import {handleChannelUpdate} from '@app/features/channel/events/ChannelUpdate';
import {handleChannelUpdateBulk} from '@app/features/channel/events/ChannelUpdateBulk';
import {handleFavoriteMemeCreate} from '@app/features/expressions/events/FavoriteMemeCreate';
import {handleFavoriteMemeDelete} from '@app/features/expressions/events/FavoriteMemeDelete';
import {handleFavoriteMemeUpdate} from '@app/features/expressions/events/FavoriteMemeUpdate';
import {handleWebhooksUpdate} from '@app/features/expressions/events/WebhooksUpdate';
import {handleReady} from '@app/features/gateway/events/GatewayReady';
import type {GatewaySocket} from '@app/features/gateway/transport/GatewaySocket';
import {handleChannelMemberCountsUpdate} from '@app/features/guild/events/ChannelMemberCountsUpdate';
import {handleGuildBanAdd, handleGuildBanRemove} from '@app/features/guild/events/GuildBan';
import {handleGuildCountsUpdate} from '@app/features/guild/events/GuildCountsUpdate';
import {handleGuildCreate} from '@app/features/guild/events/GuildCreate';
import {handleGuildDelete} from '@app/features/guild/events/GuildDelete';
import {handleGuildEmojisUpdate} from '@app/features/guild/events/GuildEmojisUpdate';
import {handleGuildHealthUpdate} from '@app/features/guild/events/GuildHealthUpdate';
import {handleGuildMemberAdd} from '@app/features/guild/events/GuildMemberAdd';
import {handleGuildMemberListUpdate} from '@app/features/guild/events/GuildMemberListUpdate';
import {handleGuildMemberRemove} from '@app/features/guild/events/GuildMemberRemove';
import {handleGuildMembersChunk} from '@app/features/guild/events/GuildMembersChunk';
import {handleGuildMemberUpdate} from '@app/features/guild/events/GuildMemberUpdate';
import {handleGuildRoleCreate} from '@app/features/guild/events/GuildRoleCreate';
import {handleGuildRoleDelete} from '@app/features/guild/events/GuildRoleDelete';
import {handleGuildRoleUpdate} from '@app/features/guild/events/GuildRoleUpdate';
import {handleGuildRoleUpdateBulk} from '@app/features/guild/events/GuildRoleUpdateBulk';
import {handleGuildStickersUpdate} from '@app/features/guild/events/GuildStickersUpdate';
import {handleGuildSync} from '@app/features/guild/events/GuildSync';
import {handleGuildUpdate} from '@app/features/guild/events/GuildUpdate';
import {handlePassiveUpdates} from '@app/features/guild/events/PassiveUpdates';
import {handleInviteCreate} from '@app/features/invite/events/InviteCreate';
import {handleInviteDelete} from '@app/features/invite/events/InviteDelete';
import {handleMessageAck} from '@app/features/messaging/events/MessageAck';
import {handleMessageCreate} from '@app/features/messaging/events/MessageCreate';
import {handleMessageDelete} from '@app/features/messaging/events/MessageDelete';
import {handleMessageDeleteBulk} from '@app/features/messaging/events/MessageDeleteBulk';
import {handleMessageReactionAdd} from '@app/features/messaging/events/MessageReactionAdd';
import {handleMessageReactionAddMany} from '@app/features/messaging/events/MessageReactionAddMany';
import {handleMessageReactionRemove} from '@app/features/messaging/events/MessageReactionRemove';
import {handleMessageReactionRemoveAll} from '@app/features/messaging/events/MessageReactionRemoveAll';
import {handleMessageReactionRemoveEmoji} from '@app/features/messaging/events/MessageReactionRemoveEmoji';
import {handleMessageUpdate} from '@app/features/messaging/events/MessageUpdate';
import {handleRecentMentionDelete} from '@app/features/messaging/events/RecentMentionDelete';
import {handleSavedMessageCreate} from '@app/features/messaging/events/SavedMessageCreate';
import {handleSavedMessageDelete} from '@app/features/messaging/events/SavedMessageDelete';
import {handleTypingStart} from '@app/features/messaging/events/TypingStart';
import {handlePresenceUpdate} from '@app/features/presence/events/PresenceUpdate';
import {handlePresenceUpdateBulk} from '@app/features/presence/events/PresenceUpdateBulk';
import {handleRelationshipAdd} from '@app/features/relationship/events/RelationshipAdd';
import {handleRelationshipRemove} from '@app/features/relationship/events/RelationshipRemove';
import {handleRelationshipUpdate} from '@app/features/relationship/events/RelationshipUpdate';
import {handleForumUnreads} from '@app/features/threads/events/ForumUnreads';
import {handleThreadCreate} from '@app/features/threads/events/ThreadCreate';
import {handleThreadDelete} from '@app/features/threads/events/ThreadDelete';
import {handleThreadListSync} from '@app/features/threads/events/ThreadListSync';
import {handleThreadMemberListUpdate} from '@app/features/threads/events/ThreadMemberListUpdate';
import {handleThreadMembersUpdate} from '@app/features/threads/events/ThreadMembersUpdate';
import {handleThreadMemberUpdate} from '@app/features/threads/events/ThreadMemberUpdate';
import {handleThreadUpdate} from '@app/features/threads/events/ThreadUpdate';
import {handleAuthSessionChange} from '@app/features/user/events/AuthSessionChange';
import {handleUserConnectionsUpdate} from '@app/features/user/events/UserConnectionsUpdate';
import {handleUserGuildSettingsUpdate} from '@app/features/user/events/UserGuildSettingsUpdate';
import {handleUserNoteUpdate} from '@app/features/user/events/UserNoteUpdate';
import {handleUserPinnedDmsUpdate} from '@app/features/user/events/UserPinnedDmsUpdate';
import {handleUserSettingsUpdate} from '@app/features/user/events/UserSettingsUpdate';
import {handleUserUpdate} from '@app/features/user/events/UserUpdate';
import {handleWebAuthnCredentialsUpdate} from '@app/features/user/events/WebAuthnCredentialsUpdate';
import {handleCallCreate} from '@app/features/voice/events/CallCreate';
import {handleCallDelete} from '@app/features/voice/events/CallDelete';
import {handleCallUpdate} from '@app/features/voice/events/CallUpdate';
import {handleEntranceSoundPlay} from '@app/features/voice/events/EntranceSoundPlay';
import {handleVoiceServerUpdate} from '@app/features/voice/events/VoiceServerUpdate';
import {handleVoiceStateUpdate} from '@app/features/voice/events/VoiceStateUpdate';

export interface GatewayGeoipPayload {
	country_code?: string;
	latitude?: string;
	longitude?: string;
}

export interface GatewayHandlerContext {
	socket: GatewaySocket | null;
	accountKey: string | null;
	expectedUserId: string | null;
	previousSessionId: string | null;
	setPreviousSessionId: (id: string) => void;
	setReady: () => boolean;
	setConnectionGeoip: (data: GatewayGeoipPayload) => void;
	markGuildSynced: (guildId: string) => void;
}

export type GatewayEventHandler = (data: unknown, context: GatewayHandlerContext) => void;
export type GatewayHandlerRegistry = Map<string, GatewayEventHandler>;

export function createHandlerRegistry(): GatewayHandlerRegistry {
	const registry: GatewayHandlerRegistry = new Map();
	registry.set('READY', handleReady as GatewayEventHandler);
	registry.set('AUTH_SESSION_CHANGE', handleAuthSessionChange as GatewayEventHandler);
	registry.set('USER_UPDATE', handleUserUpdate as GatewayEventHandler);
	registry.set('USER_SETTINGS_UPDATE', handleUserSettingsUpdate as GatewayEventHandler);
	registry.set('USER_GUILD_SETTINGS_UPDATE', handleUserGuildSettingsUpdate as GatewayEventHandler);
	registry.set('USER_PINNED_DMS_UPDATE', handleUserPinnedDmsUpdate as GatewayEventHandler);
	registry.set('USER_NOTE_UPDATE', handleUserNoteUpdate as GatewayEventHandler);
	registry.set('USER_CONNECTIONS_UPDATE', handleUserConnectionsUpdate as GatewayEventHandler);
	registry.set('WEBAUTHN_CREDENTIALS_UPDATE', handleWebAuthnCredentialsUpdate as GatewayEventHandler);
	registry.set('GUILD_CREATE', handleGuildCreate as GatewayEventHandler);
	registry.set('GUILD_UPDATE', handleGuildUpdate as GatewayEventHandler);
	registry.set('GUILD_DELETE', handleGuildDelete as GatewayEventHandler);
	registry.set('GUILD_BAN_ADD', handleGuildBanAdd as GatewayEventHandler);
	registry.set('GUILD_BAN_REMOVE', handleGuildBanRemove as GatewayEventHandler);
	registry.set('GUILD_EMOJIS_UPDATE', handleGuildEmojisUpdate as GatewayEventHandler);
	registry.set('GUILD_STICKERS_UPDATE', handleGuildStickersUpdate as GatewayEventHandler);
	registry.set('GUILD_SYNC', handleGuildSync as GatewayEventHandler);
	registry.set('GUILD_MEMBER_ADD', handleGuildMemberAdd as GatewayEventHandler);
	registry.set('GUILD_MEMBER_UPDATE', handleGuildMemberUpdate as GatewayEventHandler);
	registry.set('GUILD_MEMBER_REMOVE', handleGuildMemberRemove as GatewayEventHandler);
	registry.set('GUILD_MEMBERS_CHUNK', handleGuildMembersChunk as GatewayEventHandler);
	registry.set('GUILD_MEMBER_LIST_UPDATE', handleGuildMemberListUpdate as GatewayEventHandler);
	registry.set('GUILD_COUNTS_UPDATE', handleGuildCountsUpdate as GatewayEventHandler);
	registry.set('GUILD_HEALTH_UPDATE', handleGuildHealthUpdate as GatewayEventHandler);
	registry.set('CHANNEL_MEMBER_COUNTS_UPDATE', handleChannelMemberCountsUpdate as GatewayEventHandler);
	registry.set('GUILD_ROLE_CREATE', handleGuildRoleCreate as GatewayEventHandler);
	registry.set('GUILD_ROLE_UPDATE', handleGuildRoleUpdate as GatewayEventHandler);
	registry.set('GUILD_ROLE_DELETE', handleGuildRoleDelete as GatewayEventHandler);
	registry.set('GUILD_ROLE_UPDATE_BULK', handleGuildRoleUpdateBulk as GatewayEventHandler);
	registry.set('CHANNEL_CREATE', handleChannelCreate as GatewayEventHandler);
	registry.set('CHANNEL_UPDATE', handleChannelUpdate as GatewayEventHandler);
	registry.set('CHANNEL_UPDATE_BULK', handleChannelUpdateBulk as GatewayEventHandler);
	registry.set('CHANNEL_DELETE', handleChannelDelete as GatewayEventHandler);
	registry.set('THREAD_CREATE', handleThreadCreate as GatewayEventHandler);
	registry.set('THREAD_UPDATE', handleThreadUpdate as GatewayEventHandler);
	registry.set('THREAD_DELETE', handleThreadDelete as GatewayEventHandler);
	registry.set('THREAD_LIST_SYNC', handleThreadListSync as GatewayEventHandler);
	registry.set('THREAD_MEMBER_UPDATE', handleThreadMemberUpdate as GatewayEventHandler);
	registry.set('THREAD_MEMBERS_UPDATE', handleThreadMembersUpdate as GatewayEventHandler);
	registry.set('THREAD_MEMBER_LIST_UPDATE', handleThreadMemberListUpdate as GatewayEventHandler);
	registry.set('FORUM_UNREADS', handleForumUnreads as GatewayEventHandler);
	registry.set('PASSIVE_UPDATES', handlePassiveUpdates as GatewayEventHandler);
	registry.set('CHANNEL_PINS_UPDATE', handleChannelPinsUpdate as GatewayEventHandler);
	registry.set('CHANNEL_PINS_ACK', handleChannelPinsAck as GatewayEventHandler);
	registry.set('CHANNEL_RECIPIENT_ADD', handleChannelRecipientAdd as GatewayEventHandler);
	registry.set('CHANNEL_RECIPIENT_REMOVE', handleChannelRecipientRemove as GatewayEventHandler);
	registry.set('MESSAGE_CREATE', handleMessageCreate as GatewayEventHandler);
	registry.set('MESSAGE_UPDATE', handleMessageUpdate as GatewayEventHandler);
	registry.set('MESSAGE_DELETE', handleMessageDelete as GatewayEventHandler);
	registry.set('MESSAGE_DELETE_BULK', handleMessageDeleteBulk as GatewayEventHandler);
	registry.set('MESSAGE_ACK', handleMessageAck as GatewayEventHandler);
	registry.set('MESSAGE_REACTION_ADD', handleMessageReactionAdd as GatewayEventHandler);
	registry.set('MESSAGE_REACTION_ADD_MANY', handleMessageReactionAddMany as GatewayEventHandler);
	registry.set('MESSAGE_REACTION_REMOVE', handleMessageReactionRemove as GatewayEventHandler);
	registry.set('MESSAGE_REACTION_REMOVE_ALL', handleMessageReactionRemoveAll as GatewayEventHandler);
	registry.set('MESSAGE_REACTION_REMOVE_EMOJI', handleMessageReactionRemoveEmoji as GatewayEventHandler);
	registry.set('TYPING_START', handleTypingStart as GatewayEventHandler);
	registry.set('RECENT_MENTION_DELETE', handleRecentMentionDelete as GatewayEventHandler);
	registry.set('SAVED_MESSAGE_CREATE', handleSavedMessageCreate as GatewayEventHandler);
	registry.set('SAVED_MESSAGE_DELETE', handleSavedMessageDelete as GatewayEventHandler);
	registry.set('PRESENCE_UPDATE', handlePresenceUpdate as GatewayEventHandler);
	registry.set('PRESENCE_UPDATE_BULK', handlePresenceUpdateBulk as GatewayEventHandler);
	registry.set('VOICE_STATE_UPDATE', handleVoiceStateUpdate as GatewayEventHandler);
	registry.set('VOICE_SERVER_UPDATE', handleVoiceServerUpdate as GatewayEventHandler);
	registry.set('CALL_CREATE', handleCallCreate as GatewayEventHandler);
	registry.set('CALL_UPDATE', handleCallUpdate as GatewayEventHandler);
	registry.set('CALL_DELETE', handleCallDelete as GatewayEventHandler);
	registry.set('ENTRANCE_SOUND_PLAY', handleEntranceSoundPlay as GatewayEventHandler);
	registry.set('INVITE_CREATE', handleInviteCreate as GatewayEventHandler);
	registry.set('INVITE_DELETE', handleInviteDelete as GatewayEventHandler);
	registry.set('RELATIONSHIP_ADD', handleRelationshipAdd as GatewayEventHandler);
	registry.set('RELATIONSHIP_UPDATE', handleRelationshipUpdate as GatewayEventHandler);
	registry.set('RELATIONSHIP_REMOVE', handleRelationshipRemove as GatewayEventHandler);
	registry.set('WEBHOOKS_UPDATE', handleWebhooksUpdate as GatewayEventHandler);
	registry.set('FAVORITE_MEME_CREATE', handleFavoriteMemeCreate as GatewayEventHandler);
	registry.set('FAVORITE_MEME_UPDATE', handleFavoriteMemeUpdate as GatewayEventHandler);
	registry.set('FAVORITE_MEME_DELETE', handleFavoriteMemeDelete as GatewayEventHandler);
	registry.set('SESSIONS_REPLACE', () => {});
	return registry;
}
